/**
 * `/admin/agents/prompts` — superadmin-only list of runtime-managed
 * agent prompts (#574 PR 3).
 *
 * Organized by agent to match the sibling **Assignments** tab (#2154):
 * one card of bold-named, light-gray, collapsible rows — one per prompt
 * type (planner, generator, …, refiner) plus a `shared` "Shared rules"
 * row last. Rows default collapsed; a header click reveals that agent's
 * prompts. A search box narrows by name and auto-expands the groups that
 * still have a match. The old type-filter chips were dropped — grouping
 * by agent already does what they did, and the Assignments tab has none.
 *
 * "+ New prompt" opens the create modal. Click any prompt row to drill
 * into the detail page.
 *
 * Moved from `/admin/prompts` as part of the SuperAdmin → Agents
 * sub-nav reshape — Prompts, Models, and Budgets are all agent-behaviour
 * config and now live together under `/admin/agents/*`. The old
 * `/admin/prompts` URL still resolves via a client-side redirect in
 * `App.tsx`.
 *
 * Customer-facing visibility: NONE. Per #547's locked decisions,
 * operator-tier knobs live in the superadmin tier — the trust boundary
 * is enforced server-side by `requireSuperadmin` on every route this
 * page calls; the `<RequireSuperadmin>` route wrapper around `/admin/*`
 * is UX defense-in-depth.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@/components/ui'
import {
  adminPromptsAPI,
  ADMIN_PROMPT_TYPES,
  type AdminPromptListRow,
  type AdminPromptType,
} from '@/lib/api'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate } from '@/lib/utils'
import { AdvancedSettingsNotice } from '@/components/AdvancedSettingsNotice'
import { NewPromptDialog } from './NewPromptDialog'
import { AgentsSubNav } from '../AgentsSubNav'

const TYPE_LABEL: Record<AdminPromptType, string> = {
  planner: 'Planner',
  generator: 'Generator',
  healer: 'Healer',
  analyzer: 'Analyzer',
  auditor: 'Auditor',
  scanner: 'Scanner',
  proposer: 'Proposer',
  refiner: 'Refiner',
  dedupJudge: 'Deduper',
  shared: 'Shared rules',
}

export function PromptsPage() {
  const navigate = useNavigate()
  const tz = useUserTimezone()
  const [rows, setRows] = useState<AdminPromptListRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  // Which agent rows are expanded. Empty = all collapsed (the default,
  // matching the Assignments tab). An active search overrides this and
  // opens every matching group so results are never hidden.
  const [expanded, setExpanded] = useState<Set<AdminPromptType>>(new Set())
  const [createOpen, setCreateOpen] = useState(false)

  async function reload(): Promise<void> {
    setLoading(true)
    try {
      const r = await adminPromptsAPI.list()
      setRows(r)
      setLoadError(null)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load prompts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const needle = filter.trim().toLowerCase()
  const searching = needle.length > 0

  // Filter by name, then group by type in the canonical order. Empty
  // groups are hidden so the page doesn't show a row per type when only
  // a few match.
  const grouped = useMemo<Array<{ type: AdminPromptType; rows: AdminPromptListRow[] }>>(() => {
    if (!rows) return []
    const filtered = needle ? rows.filter(r => r.name.toLowerCase().includes(needle)) : rows
    return ADMIN_PROMPT_TYPES.map(t => ({
      type: t,
      rows: filtered.filter(r => r.type === t),
    })).filter(g => g.rows.length > 0)
  }, [rows, needle])

  function toggle(type: AdminPromptType): void {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  if (loading && !rows) {
    return <p className="text-sm text-muted-foreground">Loading prompts…</p>
  }
  if (loadError && !rows) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {loadError}
      </p>
    )
  }
  if (!rows) return null

  return (
    <div className="space-y-6 max-w-full">
      <AgentsSubNav />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Prompts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Runtime-managed prompts the agent layer fetches by name. Edits land instantly without a deploy.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>+ New prompt</Button>
      </div>

      <AdvancedSettingsNotice />

      <Card>
        <CardHeader>
          <CardTitle>Prompts by agent ({rows.length} total)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1 max-w-sm">
            <Label htmlFor="prompts-filter">Search</Label>
            <Input
              id="prompts-filter"
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by name"
              clearable
            />
          </div>

          {grouped.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              {rows.length === 0
                ? 'No prompts yet. Click "New prompt" to seed one.'
                : 'No prompts match your search.'}
            </p>
          ) : (
            <div className="space-y-2.5">
              {grouped.map(g => {
                const open = searching || expanded.has(g.type)
                // Most-recent edit across the group — a glanceable freshness
                // cue on the collapsed header, like the Assignments summary.
                const latest = g.rows.reduce(
                  (max, r) => (r.updatedAt > max ? r.updatedAt : max),
                  g.rows[0].updatedAt,
                )
                return (
                  <div key={g.type} className="border border-input rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggle(g.type)}
                      aria-expanded={open}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left bg-muted/40 hover:bg-muted/60"
                    >
                      <span
                        className={`text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
                        aria-hidden="true"
                      >
                        ▶
                      </span>
                      <span className="font-semibold">{TYPE_LABEL[g.type]}</span>
                      <span className="text-xs text-muted-foreground">
                        {g.rows.length} prompt{g.rows.length === 1 ? '' : 's'}
                      </span>
                      <span className="flex-1" />
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        updated {formatDate(latest, tz)}
                      </span>
                    </button>

                    {open && (
                      <div className="border-t border-input overflow-x-auto">
                        {/* Column header — labels the row fields (the flat
                            table this replaced had them; keep the clarity). */}
                        <div className="grid grid-cols-[minmax(220px,1fr)_64px_170px_110px_16px] items-center gap-3 px-4 py-2 pl-11 text-[10px] font-medium uppercase tracking-wide text-muted-foreground bg-muted/20">
                          <span>Name</span>
                          <span className="text-right">Active</span>
                          <span>Tags</span>
                          <span>Updated</span>
                          <span />
                        </div>
                        {g.rows.map(row => (
                          <Link
                            key={row._id}
                            to={`/admin/models/prompts/${row._id}`}
                            className="grid grid-cols-[minmax(220px,1fr)_64px_170px_110px_16px] items-center gap-3 px-4 py-2.5 pl-11 border-t border-input hover:bg-muted/30 focus-visible:bg-muted/40 focus-visible:outline-none"
                          >
                            <span className="font-mono text-xs truncate" title={row.name}>
                              {row.name}
                            </span>
                            <span className="text-right text-xs font-mono text-muted-foreground">
                              {row.activeVersion === null ? '—' : `v${row.activeVersion}`}
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {row.tags.length === 0 ? (
                                <span className="text-xs text-muted-foreground italic">none</span>
                              ) : (
                                row.tags.map(t => (
                                  <span
                                    key={t}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                                  >
                                    {t}
                                  </span>
                                ))
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(row.updatedAt, tz)}
                            </span>
                            <span className="text-muted-foreground text-xs" aria-hidden="true">›</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <NewPromptDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(promptId) => {
          setCreateOpen(false)
          navigate(`/admin/models/prompts/${promptId}`)
        }}
      />
    </div>
  )
}
